import { useCallback, useMemo, useState } from 'react';
import { excelToJson } from '../../common/common.helpers';
import { CommonProperties, MissionControlOrderDataBlob } from '../../common/common.interfaces';
import CustomError, { CustomErrorProps } from '../../common/components/CustomError';
import { useLoading } from '../../loading/LoadingContext';
import { useUserContext } from '../../user/UserContext';
import { apiFetch } from '../../utility/fetch';
import { EpicOrderDetails, EpicOrderExcelRow, ProductQueryResult } from '../epicOrders.interfaces';
import { mapEpicOrderItemsToOrder } from '../epicOrders.mapper';
import EpicOrdersImportReview from './EpicOrdersImportReview';
import EpicOrdersImportStart from './EpicOrdersImportStart';

interface EpicOrdersImportProps extends CommonProperties {} 
export default function EpicOrdersImport (props : EpicOrdersImportProps){
  const [mappedOrders, setMappedOrders] = useState(undefined)
  const [failedOrders, setFailedOrders] = useState(undefined)
  const [dataError, setDataError]: [CustomErrorProps, any] = useState(undefined)
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()
  

  // #region EXTRACT INTO USABLE FORMAT
  const handleNewOrdersUpload = useCallback(async (importParams: {timestamp: string, excelFile: File}) => {
    setDataError({
      visible: false
    })
    
    toggleLoading(true)
    changeStep('Starting Import')
    changeSubStep('Converting Excel to JSON...')
    let rows = undefined
    try {
      rows = await excelToJson<EpicOrderExcelRow>(importParams.excelFile)
    }catch(error){
      setDataError({
        content: JSON.stringify(error),
        description: `Failed to save the converted JSON file.`,
        title: 'Epic Save Failed',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    await handleJsonMapping(rows)
    toggleLoading(false)
  }, [userData])
  // # endregion
  
  // #region TRANSFORM TO MC FORMAT
  const handleJsonMapping = useCallback(async (rows: EpicOrderExcelRow[]) => {
    setDataError(undefined)
    changeStep('Requesting Mission Control Data')
    changeSubStep('Loading Product Data...')
    toggleLoading(true)

    try {
      changeSubStep('Determining Unique Orders for Rows...')
      const groupedOrders: Record<string, EpicOrderExcelRow[]> = {};
      const uniqueSkus = []
      for (const row of rows) {
        const orderNum = row.CustomerPONo
        if (!groupedOrders[orderNum]) {
          groupedOrders[orderNum] = []
        }
        groupedOrders[orderNum].push(row)
        if (!uniqueSkus.includes(row.ItemCode)) {
          uniqueSkus.push(row.ItemCode);
        }
      }
      changeSubStep('Reticulating Splines...')
      const uniqueOrderRows: EpicOrderDetails[] = Object.entries(groupedOrders).map(
        ([orderName, orderRows]) => ({
          orderName,
          orderRows
        })
      )
  
      const productNamesIn = `'${Object.values(uniqueSkus).join(`','`)}'`
      changeSubStep('Getting Listing Entries...')
      const query = `SELECT Id, (SELECT Id, Name, SKU__c FROM Listings__r WHERE Name LIKE '%EPIC' AND SKU__c IN (${productNamesIn})), (SELECT Id FROM PricebookEntries WHERE Pricebook2Id = '01s1N0000066ylwQAA') FROM Product2 WHERE Id IN (SELECT Product__c FROM Listing__c WHERE Name LIKE '%EPIC' AND SKU__c IN (${productNamesIn}))`
      const productResponse = await apiFetch<{records: ProductQueryResult[]}>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: userData.accessToken,
        body: JSON.stringify({
          soql: query
        })
      })
      if(!productResponse.ok){
        setDataError({
          content: JSON.stringify(productResponse.body),
          description: `Failed to get Pricebook Entries from Mission Control.`,
          title: 'Order Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
  
      changeSubStep('Mapping Order...')
      const mapped: MissionControlOrderDataBlob[] = [] 
      const successfulOrders = []
      const failed = []
      for (const order of uniqueOrderRows) {
        try {
          mapped.push(mapEpicOrderItemsToOrder(order, productResponse.body.records))
          successfulOrders.push(order)
        }catch(error){
          console.warn({error})
          order.error = error.toString()
          failed.push(order)
        }   
      }
      setMappedOrders(mapped)
      setFailedOrders(failed)
      if (failed.length > 0) {
        console.warn(`Some orders failed to process, please review them manually: \n${failed.map(order => JSON.stringify(order)).join('\n')}`)
      }
    }catch(error){
      setDataError({
        content: error,
        description: `Failed during Epic Mapping. ${JSON.stringify(error)}`,
        title: 'Allocation Epic Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
  }, [userData])
  // #endregion

   // #region EXTRACT INTO USABLE FORMAT
  const clearOrders = useCallback(() => {
    setMappedOrders([])
    setFailedOrders([])
  }, [])

  const handleCloseDataError = useCallback((e) => {
    setDataError({
      visible: false
    })
  }, [])

  // #region VIEW
  const view = useMemo(() => {
    
    return (
      <div>
        Epic Orders Import
        <EpicOrdersImportStart 
          handleNewOrdersUpload={handleNewOrdersUpload}
          finishOrdersUpload={clearOrders}
          mappedOrders={mappedOrders}
        />
        <EpicOrdersImportReview
          mappedOrders={mappedOrders}
          failedOrders={failedOrders}
        />
        <CustomError closeError={handleCloseDataError} content={dataError?.content} description={dataError?.description} title={dataError?.title} visible={dataError?.visible} />
      </div>
    )
  }, [mappedOrders, failedOrders])

  return view
}
